<template>
    <div
        class="discount-description"
        :class="{
            'discount-description--percentage': discount.rule_class === 'PercentageReductionPriceRule',
            'discount-description--fixed-reduction': discount.rule_class === 'FixedReductionPriceRule',
            'discount-description--absolute-price': discount.rule_class === 'AbsolutePricePriceRule',
            'discount-description--free': discount.rule_class === 'FreePriceRule',
            'discount-description--cumulative': discount.cumulative,
        }"
    >
        <div class="discount-description__header">
            <div class="discount-description__label">
                {{ discount.label }}
            </div>

            <div class="discount-description__value">
                <div class="discount-description__value-icon">
                    <mdi-icon
                        :icon="icon"
                        class="discount-description__icon"
                    />
                </div>

                <div class="discount-description__value-amount">
                    <template v-if="discount.rule_class === 'FreePriceRule'">
                        {{ t('common.free') }}
                    </template>

                    <template v-else>
                        {{ discount.rvalue.toFixed(2) }}
                    </template>
                </div>
            </div>
        </div>

        <div class="discount-description__description">
            {{ t(`price_rules.${discount.rule_class}.customer_description`, {
                value: Numbers.removeZeroDecimals(discount.rvalue.toFixed(2)),
                currency,
            }) }}
        </div>

        <div
            v-if="store.getters['account/isStaff'] && discount.description"
            class="discount-description__internal-description"
        >
            {{ discount.description }}
        </div>

        <div class="discount-description__expiry">
            <template v-if="expiry">
                {{ t('price_rules.expiry', { expiry }) }}
            </template>

            <template v-else>
                {{ t('price_rules.no_expiry') }}
            </template>
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import moment from 'moment';
import Numbers from '@/utils/Numbers';

const store = useStore();
const { t } = useI18n({ useScope: 'global' });

const props = defineProps({
    discount:
    {
        type:     Object,
        required: true,
    },
    currency:
    {
        type:     String,
        required: true,
    },
});

const icon = computed(() =>
{
    return {
        PercentageReductionPriceRule: 'mdiPercent',
        FixedReductionPriceRule:      'mdiMinus',
        AbsolutePricePriceRule:       'mdiEqual',
        FreePriceRule:                'mdiGift',
    }[props.discount.rule_class] ?? 'mdiAlertBox';
});

const expiry = computed(() =>
{
    if(props.discount.valid_until === null)
    {
        return null;
    }

    return moment(props.discount.valid_until).format('DD.MM.YYYY');
});
</script>

<style scoped>
@reference "#main.css";

.discount-description__header
{
    @apply flex justify-between items-center gap-2;
}

.discount-description__label
{
    @apply mb-1 text-gray-600 font-bold truncate;
}

.discount-description__value
{
    @apply flex justify-between items-center border border-primary-500 rounded
        bg-primary-500 overflow-hidden;
}

.discount-description__value-icon
{
    @apply px-1 py-0.5 text-white;
}

.discount-description__icon
{
    @apply w-3 h-3;
}

.discount-description__value-amount
{
    @apply px-1 bg-white text-primary-500;
}

.discount-description__description
{
    @apply text-gray-500;
}

.discount-description__internal-description
{
    @apply my-1 text-gray-500 italic;
}

.discount-description__expiry
{
    @apply text-gray-500;
}
</style>
